import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Plus, TicketCheck, ChevronRight, AlertCircle,
  Clock, RefreshCw, Filter, ArrowLeft,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { listTickets, createTicket } from '../utils/api';
import './SupportPage.css';

const CATEGORIES = [
  { value: 'wrong_data',  label: 'Wrong / Unexpected Data' },
  { value: 'ui_bug',      label: 'UI / Display Issue' },
  { value: 'performance', label: 'Slow Performance' },
  { value: 'access',      label: 'Access / Login Issue' },
  { value: 'feature',     label: 'Feature Request' },
  { value: 'other',       label: 'Other' },
];

const STATUS_META = {
  open:             { label: 'Open',             color: '#3B82F6' },
  acknowledged:     { label: 'Acknowledged',     color: '#8B5CF6' },
  in_progress:      { label: 'In Progress',      color: '#F59E0B' },
  waiting_on_user:  { label: 'Waiting on You',   color: '#EF4444' },
  resolved:         { label: 'Resolved',         color: '#10B981' },
  closed:           { label: 'Closed',           color: '#6B7280' },
  wont_fix:         { label: "Won't Fix",        color: '#DC2626' },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: '#94A3B8' };
  return (
    <span className="sp-badge" style={{ '--badge-color': meta.color }}>
      {meta.label}
    </span>
  );
}

export default function SupportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [tickets,     setTickets]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm,    setShowForm]    = useState(false);

  // New ticket form state
  const [form, setForm] = useState({ title: '', description: '', category: 'other' });
  const [submitting, setSubmitting] = useState(false);
  const [formError,  setFormError]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listTickets(statusFilter || undefined);
      setTickets(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setFormError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const ticket = await createTicket({
        title:       form.title.trim(),
        description: form.description.trim(),
        category:    form.category,
      });
      setTickets((prev) => [ticket, ...prev]);
      setShowForm(false);
      setForm({ title: '', description: '', category: 'other' });
      navigate(`/support/${ticket.ticket_ref}`);
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sp-page">
      {/* Back to chat */}
      <button className="sp-back" onClick={() => navigate('/')}>
        <ArrowLeft size={15} /> Back to Chat
      </button>

      {/* Header */}
      <div className="sp-header">
        <div className="sp-header-left">
          <TicketCheck size={22} />
          <div>
            <h1 className="sp-title">Support</h1>
            <p className="sp-sub">
              {user?.role === 'admin' ? 'All tickets for your organisation' : 'Your support tickets'}
            </p>
          </div>
        </div>
        <div className="sp-header-actions">
          <button className="sp-btn sp-btn--ghost" onClick={load}>
            <RefreshCw size={15} /> Refresh
          </button>
          <button className="sp-btn sp-btn--primary" onClick={() => setShowForm(true)}>
            <Plus size={15} /> New Ticket
          </button>
        </div>
      </div>

      {/* New ticket form modal */}
      {showForm && (
        <div className="sp-modal-overlay">
          <div className="sp-modal">
            <div className="sp-modal-header">
              <h2 className="sp-modal-title">Raise a Support Ticket</h2>
              <button
                type="button"
                className="sp-modal-close"
                aria-label="Close"
                onClick={() => setShowForm(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="sp-form">
              <label className="sp-label">
                Category
                <select
                  className="sp-select"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </label>

              <label className="sp-label">
                Short description of the issue
                <input
                  className="sp-input"
                  type="text"
                  placeholder="e.g. Sales order total is showing wrong amount"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  maxLength={200}
                />
              </label>

              <label className="sp-label">
                Full details
                <textarea
                  className="sp-textarea"
                  rows={5}
                  placeholder="Describe the issue in detail. Include what you expected vs. what you saw, the SAP question you asked, and any error messages."
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={5000}
                />
              </label>

              {formError && (
                <div className="sp-form-error">
                  <AlertCircle size={14} /> {formError}
                </div>
              )}

              <div className="sp-form-actions">
                <button
                  type="button"
                  className="sp-btn sp-btn--ghost"
                  onClick={() => { setShowForm(false); setFormError(null); }}
                >
                  Cancel
                </button>
                <button type="submit" className="sp-btn sp-btn--primary" disabled={submitting}>
                  {submitting ? 'Submitting…' : 'Submit Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="sp-filters">
        <Filter size={14} className="sp-filter-icon" />
        {['', 'open', 'acknowledged', 'in_progress', 'waiting_on_user', 'resolved', 'closed'].map((s) => (
          <button
            key={s}
            className={`sp-filter-btn ${statusFilter === s ? 'sp-filter-btn--active' : ''}`}
            onClick={() => setStatusFilter(s)}
          >
            {s === '' ? 'All' : STATUS_META[s]?.label || s}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="sp-loading">Loading tickets…</div>
      ) : error ? (
        <div className="sp-error"><AlertCircle size={16} /> {error}</div>
      ) : tickets.length === 0 ? (
        <div className="sp-empty">
          <TicketCheck size={40} className="sp-empty-icon" />
          <p>No tickets found.</p>
          <button className="sp-btn sp-btn--primary" onClick={() => setShowForm(true)}>
            Raise your first ticket
          </button>
        </div>
      ) : (
        <div className="sp-list">
          {tickets.map((t) => (
            <Link key={t.id} to={`/support/${t.ticket_ref}`} className="sp-row">
              <div className="sp-row-main">
                <div className="sp-row-ref">{t.ticket_ref}</div>
                <div className="sp-row-title">{t.title}</div>
                <div className="sp-row-meta">
                  <span className="sp-row-category">
                    {CATEGORIES.find((c) => c.value === t.category)?.label || t.category}
                  </span>
                  {user?.role === 'admin' && (
                    <span className="sp-row-user">{t.user_name || t.user_email}</span>
                  )}
                  <span className="sp-row-date">
                    <Clock size={11} />
                    {new Date(t.updated_at).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </span>
                  {t.comment_count > 0 && (
                    <span className="sp-row-comments">{t.comment_count} comments</span>
                  )}
                </div>
              </div>
              <div className="sp-row-right">
                <StatusBadge status={t.status} />
                <ChevronRight size={16} className="sp-row-arrow" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
